import { Component, OnInit } from '@angular/core';
import { StatisticsService } from "../../services/statistics.service";
import { translate, TranslocoService } from "@ngneat/transloco";
import * as moment from "moment";
import { SortOrder } from "../../model/sort-order";

type TenantStatistics = {
  name: string,
  devices: number,
  devicesWithData: number,
  percentageDevicesWithData: number,
  signals: number,
  signalsWithData: number,
  percentageSignalsWithData: number
};

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss']
})
export class StatisticsComponent implements OnInit {

  public tenantStatistics: TenantStatistics[];

  public plotOptions: any;

  public devicesGraphData: any;
  public signalsGraphData: any;

  public totalDevices: number;
  public totalDevicesWithData: number;
  public totalSignals: number;
  public totalSignalsWithData: number;

  public searchOptions: any;
  public sortOptions: any[];
  public fromDate: Date;

  public loadingStatistics: boolean;

  constructor(private statisticsService: StatisticsService,
              private translocoService: TranslocoService) {
    this.fromDate = moment().utc().subtract(7, "days").startOf('day').toDate();
    this.searchOptions = {tenant: '', sortBy: 'alphabetical', sortOrder: SortOrder.DESCENDING};
    this.buildPlotOptions();
  }

  ngOnInit(): void {
    this.loadStatistics();
    this.translocoService.selectTranslate('common.new').subscribe(() => {
      this.sortOptions = [
        {id: 'alphabetical', label: translate('misc.statistics.sort.alphabetical')},
        {id: 'byTotalDevicesWithData', label: translate('misc.statistics.sort.byTotalDevicesWithData')},
        {id: 'byPercentageDevicesWithData',  label: translate('misc.statistics.sort.byPercentageDevicesWithData')},
        {id: 'byTotalSignalsWithData', label: translate('misc.statistics.sort.byTotalSignalsWithData')},
        {id: 'byPercentageSignalsWithData', label: translate('misc.statistics.sort.byPercentageSignalsWithData')}
      ];
    });
  }

  loadStatistics() {
    this.loadingStatistics = true;
    let devicesPromise = this.statisticsService.getDevicesPerTenant(this.fromDate.getTime()).toPromise();
    let signalsPromise = this.statisticsService.getSignalsPerTenant(this.fromDate.getTime()).toPromise();
    Promise.all([devicesPromise, signalsPromise]).then(values => {
      let statistics = new Map();

      values[0].devicesPerTenant.forEach(tenant => {
        statistics.set(tenant.name, {devices: tenant.total, devicesWithData: tenant.withData});
      });

      values[1].signalsPerTenant.forEach(tenant => {
        let tenantData = statistics.get(tenant.name);
        if(!tenantData) {
          statistics.set(tenant.name, {devices: null, devicesWithData: null,
            signals: tenant.total, signalsWithData: tenant.withData});
        } else {
          tenantData.signals= tenant.total;
          tenantData.signalsWithData= tenant.withData;
        }
      });

      this.tenantStatistics = [];
      statistics.forEach((value, key) => {
        this.tenantStatistics.push({
          name: key,
          devices: value.devices,
          devicesWithData: value.devicesWithData,
          percentageDevicesWithData: value.devices > 0 ? (value.devicesWithData / value.devices) : 0,
          signals: value.signals,
          signalsWithData: value.signalsWithData,
          percentageSignalsWithData: value.signals > 0 ? (value.signalsWithData / value.signals) : 0
        });
      });
      this.loadingStatistics = false;

      this.buildGraphs();
    })
  }

  private buildGraphs() {
    this.totalDevices = 0;
    this.totalDevicesWithData = 0;
    this.totalSignals = 0;
    this.totalSignalsWithData = 0;

    this.tenantStatistics.forEach((value, key) => {
      this.totalDevices += value.devices;
      this.totalDevicesWithData += value.devicesWithData;
      this.totalSignals += value.signals;
      this.totalSignalsWithData += value.signalsWithData;
    });

    this.devicesGraphData = this.buildGraphData([
      {
        name: translate('misc.statistics.totalDevicesWithData'),
        y: this.totalDevicesWithData,
        color: '#039FE4'
      },
      {
        name: translate('misc.statistics.totalDevicesWithoutData'),
        y: this.totalDevices - this.totalDevicesWithData,
        color: '#e3e3e3'
      }
    ], translate('model.devices'));

    this.signalsGraphData = this.buildGraphData([
      {
        name: translate('misc.statistics.totalSignalsWithData'),
        y: this.totalSignalsWithData,
        color: '#039FE4'
      },
      {
        name: translate('misc.statistics.totalSignalsWithoutData'),
        y: this.totalSignals - this.totalSignalsWithData,
        color: '#e3e3e3'
      }
    ], translate('model.signals'));
  }

  buildPlotOptions() {
    this.plotOptions = {
      pie: {
        dataLabels: {
          enabled: false
        },
        startAngle: -90,
        endAngle: 90,
        center: ['50%', '95%'],
        size: '175%'
      }
    };
  }

  buildGraphData(rawData, seriesName) {
    return [{
      showInLegend: false,
      type: 'pie',
      name: seriesName,
      innerSize: '50%',
      data: rawData
    }];
  }

  get filteredTenants() {
    let result: TenantStatistics[];

    if(this.searchOptions.tenant !== '') {
      result = this.tenantStatistics.filter(item => {
        return item.name.toLowerCase().includes(this.searchOptions.tenant.toLowerCase());
      });
    } else {
      result = this.tenantStatistics;
    }

    if(this.searchOptions.sortBy === 'alphabetical') {
      result.sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
    } else if(this.searchOptions.sortBy === 'byTotalDevicesWithData') {
      result.sort((a,b) => b.devicesWithData - a.devicesWithData);
    } else if(this.searchOptions.sortBy === 'byTotalSignalsWithData') {
      result.sort((a,b) => b.signalsWithData - a.signalsWithData);
    } else if(this.searchOptions.sortBy === 'byPercentageDevicesWithData') {
      result.sort((a,b) => b.percentageDevicesWithData - a.percentageDevicesWithData);
    } else if(this.searchOptions.sortBy === 'byPercentageSignalsWithData') {
      result.sort((a,b) => b.percentageSignalsWithData - a.percentageSignalsWithData);
    }

    if(this.searchOptions.sortOrder === SortOrder.ASCENDING) {
      result.reverse();
    }

    return result;
  }

}
